import { store } from "../redux/store";
import {
  btcToSatsAmount,
  callAPIInterface,
  displaySelectedCurrencyAmount,
  ethereumLabel,
  getCurrencyAmount,
  getExchangeRate,
  lightningLabel,
  removeNonEngChar,
  solanaLabel,
  tonLabel,
  tronLabel,
  validAddress,
  withdrawRequest,
} from "./constants";
import {
  expiredInvoiceMsg,
  invalidAddressOrInvoice,
  invalidAddressSubMsg,
  invalidQrCode,
  lnurl,
  mainNetIdRequired,
  paymentMethodNotSupportedText,
  sats,
  testNetIdRequired,
  usdc,
  usdt,
  XAUT,
} from "./messages";

export const handleAddressVerification = (res) => {
  const { withdraw_request: address, network, expires_at } = res;
  let isAllowed = true;
  let headerMsg = address ? invalidAddressOrInvoice : invalidQrCode;
  let message = "";
  const env = process.env.REACT_APP_ENVIRONMENT;

  const liveMode = JSON.parse(process.env.REACT_APP_LIVEMODE);

  if (expires_at) {
    isAllowed = !(Math.floor(new Date().getTime() / 1000) > expires_at);
    message = expiredInvoiceMsg;
  }

  if (network) {
    if (env === "development") {
      if (network !== "testnet") {
        isAllowed = false;
        message = testNetIdRequired;
      }
    } else {
      if (liveMode) {
        if (network === "testnet") {
          isAllowed = false;
          message = mainNetIdRequired;
        }
      } else {
        if (network !== "testnet") {
          isAllowed = false;
          message = testNetIdRequired;
        }
      }
    }
  }

  return { isAllowed, message, headerMsg };
};

export const getAmountToDisplay = (
  method,
  amountFromURL,
  amountFromRes,
  currency,
  currencyExchangeRate
) => {
  const shouldShowBtc = store.getState()?.common?.showBtcAmount;
  switch (method) {
    case "onchain":
      if (currency === sats) {
        return shouldShowBtc
          ? amountFromURL
          : btcToSatsAmount(amountFromURL) || amountFromRes;
      } else if (currency === usdt) {
        return getCurrencyAmount(amountFromRes, currency);
      }
      break;
    case "lightning":
      if (currency === sats) {
        return displaySelectedCurrencyAmount(amountFromRes);
      } else if (currency === usdt || currency === usdc) {
        return amountFromRes * currencyExchangeRate;
      }
      break;
    case "lnurl": {
      const amt = Math.ceil(amountFromRes * currencyExchangeRate * 100) / 100;
      if (currency === sats) {
        return displaySelectedCurrencyAmount(amountFromRes);
      } else {
        return Number(amt);
      }
    }
    case "ethereum":
      return Number(amountFromURL);
    case "tron":
      return Number(amountFromURL);
    case "solana":
      return Number(amountFromURL);
    case "ton":
      return Number(amountFromURL);
  }
};

const generateRegex = (arr) => {
  const params = arr
    .reduce((acc, cur) => [...acc, cur], []) // query field and negated query field
    .join("|");

  return new RegExp("^(" + params + ")[:|=]", "i");
};

export const splitURL = (address) => {
  const withdrawRequest = address.replace(removeNonEngChar, "");

  const addresses = {};
  let extractedAddress, amount, note;
  let url;
  try {
    url = new URL(withdrawRequest);
    extractedAddress = `${url?.pathname}${url?.search}`;
  } catch (err) {
    extractedAddress = withdrawRequest;
  }
  const arr = extractedAddress?.split(/[?#&]/g)?.filter(Boolean);
  const targetCurrency = extractedAddress.split(/uuid=([^&]*)$/g)[1];

  arr?.forEach((elem) => {
    let addr = null;
    const lightningRegex = generateRegex([
      "lightning",
      "lnurl",
      "lnurlp",
      "lnurlw",
    ]);

    if (lightningRegex.test(elem)) {
      addr = elem.split(lightningRegex)?.pop();
      if (addr) addresses.lightning = addr;
      return;
    }
    const bitcoinRegex = generateRegex(["bitcoin"]);
    if (bitcoinRegex.test(elem)) {
      addr = elem.split(bitcoinRegex)?.pop();

      if (addr) addresses.bitcoin = addr;
      return;
    }

    const ethereumRegex = generateRegex(["ethereum", "address"]);
    if (ethereumRegex.test(elem)) {
      addr = elem.split(ethereumRegex)?.pop();
      if (addr) addresses.ethereum = addr;
      return;
    }
    const amountRegex = generateRegex(["amount", "uint256"]);
    if (amountRegex.test(elem)) {
      amount = elem.split(amountRegex)[2];
      return;
    }

    const noteRegex = generateRegex(["label", "note"]);
    if (noteRegex.test(elem)) {
      note = elem.split(noteRegex)[2];
      return;
    }

    if (
      !(
        elem?.includes("https") ||
        elem?.includes("/transfer") ||
        elem.includes("uuid")
      )
    )
      addresses.default = elem;
  });

  return {
    amount,
    note,
    addresses,
    targetCurrency,
  };
};

export const handleDecode = ({
  addr,
  amountFromURL,
  note,
  setIsOpenPaymentModeModal,
  setBtnLoader,
  currency: taprootAsset,
  setCurrencyExchangeRate,
  setCurrencyFromDecode,
  setAmount,
  setData,
  setNoteFromURL,
  setShowSendVia,
  setIsShowErrorModal,
  setErrorMsg,
  setErrorMsgHeader,
  address,
  payViaMethods,
  targetCurrency = null,
  isFromMiniApp = false,
  setIsMemoDrawerOpen,
}) => {
  setIsOpenPaymentModeModal?.(false);
  const isValid = validAddress.test(addr);
  if (isValid) {
    const data = {
      withdraw_request: addr,
    };

    if (addr) {
      setBtnLoader?.(true);
      callAPIInterface("POST", "/withdraws/decode", data)
        .then(async (res) => {
          if (res) {
            const { isAllowed, message, headerMsg } =
              handleAddressVerification(res);
            let currencyToSend;
            const withdrawMethod = res?.withdraw_method;

            if (withdrawMethod === tonLabel) {
              setIsMemoDrawerOpen?.(true);
            }
            //if the selected taproot asset is not having payment_method enabled, which is returned from decode API then select the first asset which is having that payment_method enabled.

            let responseWithdrawMethod =
              res?.withdraw_method === lnurl
                ? lightningLabel
                : res?.withdraw_method;

            let currency = payViaMethods?.[responseWithdrawMethod]?.some(
              (method) => method.name === taprootAsset
            )
              ? taprootAsset
              : payViaMethods?.[responseWithdrawMethod]?.[0]?.name ||
                taprootAsset;

            // get TargetCurrency
            switch (withdrawMethod) {
              case lightningLabel:
                const isZeroAmount = res?.amount === 0 || !res?.amount;

                currencyToSend =
                  res?.amount !== 0
                    ? currency
                    : isZeroAmount
                    ? sats
                    : payViaMethods?.[res?.withdraw_method]?.[0]?.name || sats;
                break;
              case ethereumLabel:
                const isUSDC = currency === usdc || targetCurrency === usdc;
                const isUSDT = currency === usdt || targetCurrency === usdt;

                if (isUSDC) {
                  currencyToSend = usdc;
                } else if (isUSDT) {
                  currencyToSend = usdt;
                } else {
                  currencyToSend = XAUT;
                }
                break;
              case solanaLabel:
                currencyToSend =
                  currency === usdc || targetCurrency === usdc ? usdc : usdt;
                break;
              case tronLabel:
                currencyToSend = usdt;
                break;
              case tonLabel:
                currencyToSend = usdt;
                break;
              case lnurl:
                if (res?.tag === withdrawRequest) {
                  currencyToSend = sats;
                } else {
                  currencyToSend =
                    res?.min_sendable === res?.max_sendable ? sats : currency;
                }
                break;
              default:
                currencyToSend = res?.currency;
            }

            let currencyExchangeRate;

            if (res?.currency === sats && currencyToSend !== res?.currency) {
              const response = await getExchangeRate(
                res?.currency,
                currencyToSend
              );
              currencyExchangeRate = response?.target_lowest_rate;
              setCurrencyExchangeRate?.(response?.target_lowest_rate);
            }

            setCurrencyFromDecode?.(currencyToSend);

            if (isAllowed) {
              let amountToDisplay = 0;

              if (res?.withdraw_method === lnurl) {
                if (res?.tag === withdrawRequest) {
                  amountToDisplay = displaySelectedCurrencyAmount(
                    isFromMiniApp
                      ? res?.max_withdrawable
                      : res?.min_withdrawable
                  );
                } else {
                  amountToDisplay = getAmountToDisplay(
                    res?.withdraw_method,
                    amountFromURL,
                    res?.min_sendable,
                    currencyToSend,
                    currencyExchangeRate
                  );
                }
              } else {
                amountToDisplay = getAmountToDisplay(
                  res?.withdraw_method,
                  amountFromURL,
                  res?.amount,
                  currencyToSend,
                  currencyExchangeRate
                );
              }

              setAmount?.(amountToDisplay || null);
              setData?.(res);
              setNoteFromURL?.(note?.replaceAll("+", " ") || res?.description);
              setShowSendVia?.(true);
              setBtnLoader?.(false);

              if (
                !payViaMethods[responseWithdrawMethod] ||
                (payViaMethods[responseWithdrawMethod] &&
                  amountToDisplay === 0 &&
                  !payViaMethods[responseWithdrawMethod]?.some(
                    (method) => method.name === sats
                  ))
              ) {
                setIsShowErrorModal(true);
                setErrorMsg?.(paymentMethodNotSupportedText);
                return;
              }
            } else {
              setBtnLoader?.(false);
              setIsShowErrorModal?.(true);
              setErrorMsg?.(message);
              setErrorMsgHeader?.(headerMsg);
            }
          }
        })
        .catch((e) => {
          setBtnLoader?.(false);
          if (e?.response?.status === 400) {
            setIsShowErrorModal?.(true);
            setErrorMsg?.(invalidAddressSubMsg);
            setErrorMsgHeader?.(invalidAddressOrInvoice);
          }
        });
    }
  } else {
    setIsShowErrorModal?.(true);
    setErrorMsg?.(invalidAddressSubMsg);
    setErrorMsgHeader?.(address ? invalidAddressOrInvoice : invalidQrCode);
  }
};

export const handleProceed = ({
  invoiceUrl,
  setNoteFromURL,
  setLightningAddress,
  setSecondBitcoinAddress,
  setAmount,
  setIsOpenPaymentModeModal,
  ...props
}) => {
  const { amount, addresses, note, targetCurrency } = splitURL(invoiceUrl);
  setNoteFromURL?.(note);

  setLightningAddress?.(addresses?.lightning || addresses?.default);
  setSecondBitcoinAddress?.(addresses?.bitcoin || addresses?.default);
  if (Object.keys(addresses).length > 1) {
    setAmount?.(amount);
    setIsOpenPaymentModeModal?.(true);
  } else {
    const extractedAddress = Object.values(addresses)?.[0];

    handleDecode({
      addr: extractedAddress,
      amountFromURL: amount,
      note,
      targetCurrency,
      setNoteFromURL,
      setAmount,
      setIsOpenPaymentModeModal,
      ...props,
    });
  }
};
